// SPDX-License-Identifier: GPL-3.0-or-later
// The run save. It lives under `pinball.run.v1` in local storage. It holds the best final score,
// the highest unlocked stage, the best points of each stage and the clear count. It never decays
// and it never leaves the device.
//
// When the run save is missing or bad, the reader takes the best of the old `pinball.best.v1` key.
// The reader leaves that old key as it is, so a rollback still reads it.
// A save with a version above 1 comes from a newer build. It is read with defaults (the old best
// key still counts) and never written.
// A storage that is blocked or full never breaks the game.

import { STAGE_COUNT } from "../src/table/run/stages.ts";
import type { RunSave, RunSaveKey } from "../src/table/run/types.ts";

/** The old local storage key of the best score. The number is the version of the stored value. */
export const BEST_KEY = "pinball.best.v1";

/** The local storage key of the run save. The number is the version. */
export const RUN_KEY: RunSaveKey = "pinball.run.v1";

/** A stored value longer than this is bad. */
const MAX_CHARS = 4_096;

/** The part of `Storage` that the module uses. */
export interface BestStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** A score that is a whole number of 0 or more. Anything else gives 0. */
export function cleanScore(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
}

/** Parse the text of the old best key: 1 to 15 digits. Anything else gives 0. */
export function parseBestText(raw: unknown): number {
  return typeof raw === "string" && /^\d{1,15}$/.test(raw) ? cleanScore(Number(raw)) : 0;
}

/** A change to record after a game or a stage. Each field is optional. */
export interface RunSavePatch {
  /** The final score of a game. It is kept only when higher than the saved best. */
  readonly best?: number;
  /** The stage points of one stage, by 0-based index. Kept only when higher. */
  readonly stagePoints?: { readonly index: number; readonly points: number };
  /** The 1-based number of a cleared stage. It unlocks the next stage at once. */
  readonly clearedStage?: number;
}

/** The result of a write. The cue flags are false on the first record of a field. */
export interface RunWrite {
  /** The save after the write. */
  readonly save: RunSave;
  /** True when the final score beat an earlier best above 0. */
  readonly newBest: boolean;
  /** True when the stage points beat an earlier stage best above 0. */
  readonly newStageBest: boolean;
}

/** The run save store. */
export interface RunSaveStore {
  /** Read the save now. */
  read(): RunSave;
  /** Merge a patch into the save and store it. */
  write(patch: RunSavePatch): RunWrite;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A whole number of 0 or more up to `Number.MAX_SAFE_INTEGER`. Anything else gives 0. */
function cleanCount(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

/** A stage number of 1 to `STAGE_COUNT`. */
function isStageNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= STAGE_COUNT;
}

/** A 0-based stage index of 0 to `STAGE_COUNT - 1`. */
function isStageIndex(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value < STAGE_COUNT;
}

/** The unlock level of 1 to `STAGE_COUNT`. */
function cleanUnlock(value: unknown): number {
  return Math.min(STAGE_COUNT, Math.max(1, cleanCount(value)));
}

/** Exactly `STAGE_COUNT` clean counts. It pads with 0 and cuts the extra items. */
function cleanStages(value: unknown): number[] {
  const list: unknown[] = Array.isArray(value) ? value : [];
  return Array.from({ length: STAGE_COUNT }, (_, i) => cleanCount(list[i]));
}

/** The save of a new player. The legacy best is the start value. */
function defaults(legacyBest: number): RunSave {
  return { v: 1, best: legacyBest, unlocked: 1, stageBest: Array(STAGE_COUNT).fill(0), clears: 0 };
}

/** Read the old best key. A missing, bad or blocked value gives 0. */
function readLegacyBest(storage: BestStorage | null): number {
  try {
    return parseBestText(storage?.getItem(BEST_KEY));
  } catch {
    return 0;
  }
}

/** Read the run key. A missing or blocked value gives null. */
function readText(storage: BestStorage | null): unknown {
  try {
    return storage?.getItem(RUN_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Parse the stored text. A too long, bad or non-object value gives null. */
function parseSave(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== "string" || raw.length > MAX_CHARS) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Clean each field of a version 1 save. The best never falls below the legacy best. */
function cleanSave(obj: Record<string, unknown>, legacyBest: number): RunSave {
  return {
    v: 1,
    best: Math.max(cleanCount(obj.best), legacyBest),
    unlocked: cleanUnlock(obj.unlocked),
    stageBest: cleanStages(obj.stageBest),
    clears: cleanCount(obj.clears),
  };
}

/** Read the save and say whether it must stay unwritten. */
function readState(storage: BestStorage | null): { save: RunSave; readOnly: boolean } {
  const obj = parseSave(readText(storage));
  const legacyBest = readLegacyBest(storage);
  if (obj && typeof obj.v === "number" && obj.v > 1) {
    return { save: defaults(legacyBest), readOnly: true };
  }
  if (!obj || obj.v !== 1) return { save: defaults(legacyBest), readOnly: false };
  return { save: cleanSave(obj, legacyBest), readOnly: false };
}

/** Merge one score. It is a new best only when the earlier value is above 0. */
function mergeBest(prev: number, value: unknown): { value: number; isNew: boolean } {
  const clean = cleanScore(value);
  if (clean <= prev) return { value: prev, isNew: false };
  return { value: clean, isNew: prev > 0 };
}

/** Merge a patch into a save. It uses the max of each field and never lowers a value. */
function applyPatch(prev: RunSave, patch: RunSavePatch): RunWrite {
  const best = mergeBest(prev.best, patch.best);
  const stageBest = [...prev.stageBest];
  let newStageBest = false;
  if (patch.stagePoints && isStageIndex(patch.stagePoints.index)) {
    const merged = mergeBest(stageBest[patch.stagePoints.index] ?? 0, patch.stagePoints.points);
    stageBest[patch.stagePoints.index] = merged.value;
    newStageBest = merged.isNew;
  }
  const cleared = isStageNumber(patch.clearedStage) ? patch.clearedStage : 0;
  const save: RunSave = {
    v: 1,
    best: best.value,
    unlocked:
      cleared > 0 ? Math.max(prev.unlocked, Math.min(STAGE_COUNT, cleared + 1)) : prev.unlocked,
    stageBest,
    clears: cleared === STAGE_COUNT ? prev.clears + 1 : prev.clears,
  };
  return { save, newBest: best.isNew, newStageBest };
}

/** Store the save. A blocked or full storage keeps the save for this page only. */
function saveText(storage: BestStorage | null, save: RunSave): void {
  try {
    storage?.setItem(RUN_KEY, JSON.stringify(save));
  } catch {
    // The storage is blocked or full. The game goes on.
  }
}

/** Read the run save now. A bad, missing or blocked value gives the defaults. */
export function loadRunSave(storage: BestStorage | null): RunSave {
  return readState(storage).save;
}

/** Make the run save store. Each write reads the storage again first. */
export function createRunSaveStore(storage: BestStorage | null): RunSaveStore {
  return {
    read: () => loadRunSave(storage),
    write(patch) {
      const state = readState(storage);
      const result = applyPatch(state.save, patch);
      if (!state.readOnly) saveText(storage, result.save);
      return result;
    },
  };
}
